import moment from "moment";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import OtpInput from "react-otp-input";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { actionsAuth } from "../../redux/auth.slice";
import withAuth from "../../services/withAuthProvider";
import withoutAuth from "../../services/withoutAuthProvider";

const SignIn = () => {
  const location = useLocation();
  // Email tidak lagi diambil dari URL (dulu /otp/:email) — dikirim via router state
  // saat redirect dari login, agar email (PII) tak terekspos/terindeks di URL.
  const email = location?.state?.email;

  const [otp, setOtp] = useState("");
  const [isLoadingSend, setIsLoadingSend] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isDisabledResend, setIsDisabledResend] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);
  const { push } = useHistory();
  const dispatch = useDispatch();

  const formatTime = (seconds) => {
    if (seconds === null || Number.isNaN(seconds)) {
      return "00:00";
    }
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  function calculateResendTimeout(resendTimeoutAt) {
    if (!resendTimeoutAt) {
      setTimeLeft(120); // Default 2 menit
      return;
    }

    // Ambil waktu sekarang
    const currentTime = moment();
    // Parse resend timeout (sudah dalam format WIB/GMT+7)
    const timeoutMoment = moment(resendTimeoutAt);
    // Hitung selisih dalam detik
    const diffSeconds = timeoutMoment.diff(currentTime, "seconds");
    const finalTime = diffSeconds < 0 ? 0 : diffSeconds;
    setTimeLeft(finalTime);
  }

  useEffect(() => {
    const fetchOtp = async () => {
      const otpPageVisited = sessionStorage.getItem("otpPageVisited");
      const forceNewOtp = location?.state?.forceNewOtp;

      // PRIORITAS 1: Jika halaman di-reload (sudah pernah dikunjungi) DAN tidak ada flag forceNewOtp
      // JANGAN request OTP baru, gunakan localStorage yang ada
      if (otpPageVisited && !forceNewOtp) {
        const resendTimeoutAt = localStorage.getItem("resendTimeoutAt");

        if (resendTimeoutAt) {
          const currentTime = moment();
          const timeoutMoment = moment(resendTimeoutAt);
          const diffSeconds = timeoutMoment.diff(currentTime, "seconds");

          setIsLoadingSend(false);

          // Jika masih valid (belum timeout), gunakan waktu yang tersimpan
          if (diffSeconds > 0) {
            calculateResendTimeout(resendTimeoutAt);
          } else {
            // Jika sudah timeout, set timer ke 0 (tampilkan tombol resend)
            setTimeLeft(0);
            setIsDisabledResend(false);
          }
        } else {
          // Tidak ada localStorage, set state default
          setIsLoadingSend(false);
          setTimeLeft(0);
          setIsDisabledResend(false);
        }
        return;
      }

      // Guard: hanya kirim OTP bila memang datang dari proses login (state forceNewOtp + email).
      // Akses langsung ke URL /otp (mis. crawler/bot atau tab baru tanpa login) tidak punya
      // router state → TIDAK boleh memicu pengiriman OTP (cegah spam/email-bombing).
      if (!forceNewOtp || !email) {
        setIsLoadingSend(false);
        push("/masuk");
        return;
      }

      // PRIORITAS 2: First visit (redirect dari login) ATAU forceNewOtp = true
      // SELALU request OTP baru, hapus localStorage lama (jika ada)
      localStorage.removeItem("resendTimeoutAt");
      sessionStorage.removeItem("otpPageVisited");

      const dataBody = {
        email: email,
      };
      const response = await withoutAuth.postDataWithoutAuth("/api/otp/send", dataBody);
      if (response) {
        setIsLoadingSend(false);
      }
      if (response?.status === 200) {
        // Cek apakah ada field timeout_at_original untuk resend (2 menit)
        // Jika tidak ada, buat sendiri 2 menit dari sekarang
        let resendTimeout;
        if (response.data?.data?.timeout_at_original) {
          resendTimeout = response.data?.data?.timeout_at_original;
        } else {
          // Buat waktu 2 menit dari sekarang dalam format ISO
          resendTimeout = moment().add(2, "minutes").toISOString();
        }

        localStorage.setItem("resendTimeoutAt", resendTimeout);
        calculateResendTimeout(resendTimeout);

        // Tandai bahwa halaman sudah pernah dikunjungi
        sessionStorage.setItem("otpPageVisited", "true");
      } else {
        toast.error(response?.data?.message, {
          id: "error-message",
          duration: 3000,
        });
      }
    };

    fetchOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (otp && otp.length === 6) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [otp]);

  useEffect(() => {
    if (timeLeft === 0) {
      setIsDisabledResend(false);
      // JANGAN hapus localStorage, biarkan tetap tersimpan
      // Ini membuat timer tetap 00:00 setelah reload
      return;
    }

    if (timeLeft > 0) {
      setIsDisabledResend(true);
      const interval = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000); // Update every second

      return () => clearInterval(interval);
    }
  }, [timeLeft]);

  const handleResend = async () => {
    setIsLoadingSend(true);

    const dataBody = {
      email: email,
    };

    const response = await withoutAuth.postDataWithoutAuth("/api/otp/resend", dataBody);

    if (response) {
      setIsLoadingSend(false);
    }

    if (response?.status !== 200) {
      toast.error(response?.data?.message || "Gagal mengirim ulang OTP. Silakan coba lagi.", {
        id: "error-message",
        duration: 3000,
      });
    } else {
      // Cek apakah ada field timeout_at_original untuk resend (2 menit)
      // Jika tidak ada, buat sendiri 2 menit dari sekarang
      let resendTimeout;
      if (response.data?.data?.timeout_at_original) {
        resendTimeout = response.data?.data?.timeout_at_original;
      } else {
        // Buat waktu 2 menit dari sekarang dalam format ISO
        resendTimeout = moment().add(2, "minutes").toISOString();
      }

      localStorage.setItem("resendTimeoutAt", resendTimeout);
      calculateResendTimeout(resendTimeout);
      setIsDisabledResend(true);

      toast.success(response?.data?.message, {
        id: "success-message",
        duration: 3000,
      });

      setOtp("");
    }
  };

  const handleSubmit = async () => {
    setIsLoadingSend(true);

    const dataVerify = {
      email: email,
      otp: otp,
    };

    const response = await withoutAuth.postDataWithoutAuth("/api/otp/verify", dataVerify);
    if (response) {
      setIsLoadingSend(false);

      if (response?.status === 200) {
        //Set token to withAuthProvider
        withAuth.setToken(response.data.data.accessToken);

        const responseUserData = await withAuth.getDataWithAuth("/api/account/user", {});
        if (responseUserData?.status === 200) {
          dispatch(
            actionsAuth.setUser({
              token: response.data.data.accessToken,
              user: responseUserData.data.data,
            })
          );
        }
      } else {
        if (
          typeof response.data.message === "object" &&
          response.data.message !== null &&
          !Array.isArray(response.data.message)
        ) {
          var data_message = "";
          for (var key in response.data.message) {
            if (response.data.message.hasOwnProperty(key)) {
              data_message += response.data.message[key];
            }
          }
          toast.error(data_message, {
            id: "error-message",
            duration: 3000,
          });
        } else {
          if (
            response?.status === 403 &&
            response?.data?.message === "Demi perlindungan akun Anda, silakan perbarui kata sandi Anda"
          ) {
            push("/lupa-password");
          }

          if (response?.status === 400 && response.data.message === "Email belum terverifikasi") {
            push("/verifikasi-akun", {
              dataParams: {
                email: email,
              },
            });
          } else {
            toast.error(response.data.message, {
              id: "error-message",
              duration: 3000,
            });
          }
        }
      }
    } else {
      toast.error("Gagal koneksi ke server", {
        id: "error-message",
        duration: 3000,
      });
    }
  };

  return (
    <div className="w-100">
      <p className="fw-bold fs-4 mb-4">Verifikasi OTP</p>
      <div className="font-sm d-flex mb-4" style={{ justifyContent: "space-between" }}>
        <div className="me-2 text-secondary">
          Masukkan kode OTP Verifikasi yang telah dikirim ke alamat email <b>{email}</b>
        </div>
      </div>
      <div className="w-100">
        <OtpInput
          value={otp}
          onChange={setOtp}
          numInputs={6}
          renderSeparator={<span style={{ width: "100px" }}></span>}
          renderInput={(props) => <input {...props} />}
          inputType="number"
          inputStyle={{
            width: "100%",
            height: "50px",
            borderRadius: "10px",
            border: "1px solid gray",
          }}
        />

        <div className="d-grid gap-2 mt-4 mb-4">
          <button
            className="btn btn-primary font-sm"
            type="button"
            onClick={handleSubmit}
            disabled={isDisabled || isLoadingSend}
          >
            Verifikasi OTP
          </button>
        </div>

        {isDisabledResend ? (
          <div className="font-sm d-flex mb-4" style={{ justifyContent: "center" }}>
            <div className="me-2 text-secondary">Kirim ulang kode setelah</div>
            <div className="text-primary">{formatTime(timeLeft)}</div>
          </div>
        ) : (
          <div className="font-sm d-flex mb-3" style={{ justifyContent: "center" }}>
            <div className="me-2 text-secondary">Belum menerima OTP Code?</div>
            <div className="text-primary cursor-pointer" onClick={handleResend}>
              Resend
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignIn;
